/* eslint-disable no-magic-numbers */
import {
    FormControlLabel,
    Grid,
    Radio,
    Accordion,
    AccordionSummary,
    IconButton,
    Typography,
    Box,
    RadioGroup,
    Divider,
    AccordionDetails,
} from "@mui/material"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import { useEffect, useState } from "react"
import moment from "moment"
import { MembershipDetail } from "../../../services/ntuc/memberService/membershipDetails"
import { LoadingButton } from "@mui/lab"
import { useTranslation } from "react-i18next"
import { MembershipPaymentRoute } from "../MembershipPaymentFlow/MembershipPaymentFlow"
import { useCookies } from "react-cookie"
import { AppCookies } from "../../../helpers/cookies"

export type MembershipPaymentRouteProps = {
    onNavigateTo: (newRoute: MembershipPaymentRoute) => void
}

type MembershipSelectionProps = MembershipPaymentRouteProps & {
    memberships?: MembershipDetail[] | undefined
    originalRoute: MembershipPaymentRoute
}

const MembershipSelection = (props: MembershipSelectionProps) => {
    const [, setCookie] = useCookies()
    const { onNavigateTo, memberships, originalRoute } = props
    const [loading] = useState(false)
    const {t} = useTranslation(`arrears-payment`)
    const [membershipExpanded, setMembershipExpanded] = useState<boolean[]>([])
    const [selectedMembership, setSelectedMembership] = useState<MembershipDetail | undefined>(
        undefined,
    )

    useEffect(() => {
        if (selectedMembership) {
            window.myNtucConfig.membershipId = (selectedMembership.id).toString()
        }
    }, [selectedMembership])

    const getMemberStartDate = (membership: MembershipDetail) => {
        return `${moment(membership.memberStartDate).format(`DD MMM YYYY`)}`
    }

    const getMemberLastPaidPeriod = (membership: MembershipDetail) => {
        return `${moment(membership.lastPaymentEndDate).format(`DD MMM YYYY`)}`
    }

    const handleMembershipSelection = (membership: MembershipDetail, index: number) => {
        setSelectedMembership(membership)
        setMembershipExpanded((prev) => {
            const updated = [...prev]
            updated.fill(false)
            updated[index] = true
            return updated
        })
    }

    const handleNextButton = () => {
        setCookie(AppCookies.MYNTUC_MEMBERSHIP_ID, selectedMembership?.id)
        onNavigateTo(originalRoute)
    }

    const GetMembershipRows = () => {
        if (memberships) {
            return (<>
                {` `}
                { memberships.map((membership, index) => {
                    return (
                        <Grid key={index} item xs={12} marginBottom={2}>
                            <Accordion disableGutters={true} expanded={membershipExpanded[index]}>
                                <AccordionSummary
                                    expandIcon={
                                        <IconButton
                                            onClick={() => {
                                                setMembershipExpanded((prev) => {
                                                    const clonedPrev = [...prev]
                                                    clonedPrev[index] = !clonedPrev[index]
                                                    return clonedPrev
                                                })
                                            }}
                                        >
                                            <ArrowDropDownIcon />
                                        </IconButton>
                                    }
                                    aria-controls={`panel${index}-content`}
                                    id={`panel${index}-header`}
                                    sx={{ cursor: `unset !important`, '& .MuiAccordionSummary-content': { minWidth: 0 } }}
                                >
                                    <Grid item xs zeroMinWidth>

                                        <FormControlLabel
                                            value={membership}
                                            control={<Radio />}
                                            sx={{ m: 0, width: `100%`, '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 } }}
                                            label={
                                                <Grid container alignItems="center" spacing={1} wrap="nowrap">
                                                    {/* Left side: Radio + Icon */}
                                                    <Grid item>
                                                        <Box sx={{ display: `flex`, alignItems: `center` }}>
                                                            <Box
                                                                sx={{
                                                                    borderRadius: `50%`,
                                                                    width: 40,
                                                                    height: 40,
                                                                    display: `flex`,
                                                                    alignItems: `center`,
                                                                    justifyContent: `center`,
                                                                    overflow: `hidden`,
                                                                    ml: 1,
                                                                }}
                                                            >
                                                                <img
                                                                    src={`${window.myNtucConfig.assetsBaseUrl}${membership.union.logo.thumbnail}`}
                                                                    alt="Membership Icon"
                                                                    style={{ width: 20, height: 20 }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Grid>

                                                    {/* Right side: Text */}
                                                    <Grid item xs zeroMinWidth>
                                                        <Typography
                                                            variant="subtitle1"
                                                            fontWeight="bold"
                                                        >
                                                            {membership.unionName}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" fontWeight="bold">
                                                            {`Member since: ${getMemberStartDate(membership)}`}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            }
                                            onClick={() => {
                                                handleMembershipSelection(membership, index)
                                            }}
                                        />

                                    </Grid>
                                </AccordionSummary>
                                {(membership?.lastPaymentEndDate || membership?.paymentMode || membership?.status) && (
                                    <AccordionDetails>
                                        <Grid item xs={12} marginTop={1} paddingLeft={{ xs: 2, sm: 12 }}>
                                            <Typography variant="body2">
                                                {`Status: ${membership.status}`}
                                            </Typography>
                                            <Typography variant="body2">
                                                {`Recurring payment method: ${membership.paymentTypeName || membership.paymentModeName || `-`}`}
                                            </Typography>
                                            <Typography variant="body2">
                                                {`Paid until: ${getMemberLastPaidPeriod(membership)}`}
                                            </Typography>
                                        </Grid>
                                    </AccordionDetails>
                                )}
                            </Accordion>
                        </Grid>
                    )
                })}
            </>
            )
        }
        return (<></>)
    }

    const ShowAllMemberships = () => {
        return (
            <Grid item xs={12} justifyContent="center">
                <RadioGroup
                    row
                    aria-labelledby="arrear-radio-buttons-group-label"
                    name="radio-buttons-group"
                    value={selectedMembership}
                >
                    <GetMembershipRows />
                </RadioGroup>
            </Grid>
        )
    }

    return (
        <>
            <Grid
                container
                justifyContent="center"
                padding={{ xs: 2, sm: 6 }}
                rowSpacing={2}
                columnSpacing={{
                    xs: `16px`,
                    sm: `24px`,
                    md: `24px`,
                    lg: `40px`,
                }}
                sx={{ backgroundColor: `white`}}
            >
                <Grid item xs={12}>
                    <Typography variant="h2" align="center" paddingBottom={5}>
                        {`Membership Selection`}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body1"></Typography>
                    <Typography mb={1} variant="body2" fontWeight="bold">
                        {`You have more than one memberships. Select the union to see more options`}
                    </Typography>
                </Grid>
                <Grid item xs={12} md={6} />
                <Grid item xs={12}>
                    <Typography variant="h3">
                        {t(`title`)}
                    </Typography>
                    <Divider />
                </Grid>
                <ShowAllMemberships />
                <Grid
                    container
                    justifyContent="space-evenly"
                    alignItems="center"
                    padding={5}
                >
                    <LoadingButton
                        variant="contained"
                        color="primary"
                        size="large"
                        loading={loading}
                        disabled={selectedMembership == undefined}
                        onClick={handleNextButton}
                    >
                        {t(`next`, { ns: `common` })}
                    </LoadingButton>
                </Grid>
            </Grid>
        </>
    )

}

export default MembershipSelection
